/**
 * API Error Handler
 *
 * Centralized error handling for API requests with smart user notifications.
 * Integrates with apiErrorManager for deduplication and toast notifications.
 *
 * Features:
 * - Status-code-based error handling (401, 403, 404, 422, 429, 5xx)
 * - Smart deduplication to prevent notification spam
 * - Expandable error details for debugging
 * - Auth error handling with logout action
 * - Graceful handling of expected errors (404s)
 *
 * @see src/services/apiErrorManager.ts Deduplication logic
 * @see src/components/ErrorDetailsButton.tsx Error details UI
 */

import { AxiosError } from 'axios';
import { apiErrorManager } from './apiErrorManager';
import React from 'react';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';

// Types for error handler
export interface ApiErrorContext {
  showError: (message: string, options?: any) => void;
  showWarning: (message: string, options?: any) => void;
  showInfo: (message: string, options?: any) => void;
  signOut?: () => void;
  ErrorDetailsButton?: React.ComponentType<{ error: AxiosError; snackbarKey: any }>;
}

let errorContext: ApiErrorContext | null = null;

/**
 * Initialize error handler with notification context
 * Must be called before error handling works
 */
export const initializeErrorHandler = (context: ApiErrorContext) => {
  errorContext = context;
};

/**
 * Get user-friendly error message from response. Reads rails-api's plural
 * `errors` array in addition to the singular shapes so the actionable reason
 * survives forwarding instead of degrading to axios's stock message.
 */
const getErrorMessage = (error: AxiosError): string => {
  const responseData = error.response?.data as any;
  if (typeof responseData?.detail === 'string' && responseData.detail.trim()) {
    return responseData.detail;
  }
  return extractApiErrorMessage(error, 'An error occurred');
};

/**
 * Logout button component for auth error notifications
 * Defined outside handlers to prevent recreation on each call
 */
const LogoutButton = () => (
  <button
    onClick={() => errorContext?.signOut?.()}
    style={{
      background: 'transparent',
      border: 'none',
      color: 'inherit',
      textDecoration: 'underline',
      cursor: 'pointer',
      padding: '4px 8px',
      fontSize: 'inherit',
    }}
  >
    Log Out
  </button>
);

/**
 * Handle authentication errors (401)
 */
const handleAuthError = () => {
  if (!errorContext) return;

  errorContext.showWarning(
    'Session may have expired. If issues persist, please log out and log back in.',
    {
      persist: true,
      action: <LogoutButton />,
    },
  );
};

/**
 * Handle API errors with smart notifications
 * Called by axios interceptor
 */
export const handleApiError = (error: AxiosError): void => {
  if (!errorContext) {
    console.warn('API error handler not initialized');
    return;
  }

  const status = error.response?.status || 0;
  const config = error.config;

  // A caller that reports its own outcome (e.g. per-member bulk results) opts out of this toast.
  // 401 still signs the user out.
  if (
    (config as { skipErrorNotification?: boolean } | undefined)?.skipErrorNotification &&
    status !== 401
  ) {
    return;
  }

  // Check if we should show this error (deduplication)
  const shouldShow = apiErrorManager.shouldShowError({
    url: config?.url || '',
    status,
    method: config?.method,
  });

  if (!shouldShow) {
    // Skip - already shown recently
    return;
  }

  // Handle by status code
  switch (status) {
    case 401: // Unauthorized
      handleAuthError();
      break;

    case 403: // Forbidden
      errorContext.showWarning("You don't have permission to perform this action", {
        autoHideDuration: 6000,
      });
      break;

    case 404: // Not Found
      // Often expected (checking if resource exists)
      // Don't show notification - let component handle it
      break;

    case 422: // Validation Error
      {
        const message = getErrorMessage(error);
        errorContext.showError(message || 'Invalid data provided', {
          autoHideDuration: 6000,
        });
      }
      break;

    case 429: // Rate Limited
      errorContext.showWarning('Too many requests. Please wait a moment and try again.', {
        autoHideDuration: 8000,
      });
      break;

    case 500: // Internal Server Error
    case 502: // Bad Gateway
    case 503: // Service Unavailable
    case 504: // Gateway Timeout
      {
        const DetailsButton = errorContext.ErrorDetailsButton;

        // Use warning color (orange) instead of error (red) for softer appearance
        if (DetailsButton) {
          // Show custom expandable error component
          errorContext.showWarning('', {
            autoHideDuration: 12000, // Longer duration for expandable content
            action: (key: string | number) => <DetailsButton error={error} snackbarKey={key} />,
          });
        } else {
          // Fallback to simple message if component not available
          const message =
            status >= 500 && status < 600
              ? 'Server error occurred. Our team has been notified.'
              : getErrorMessage(error);
          errorContext.showError(message, {
            autoHideDuration: 8000,
          });
        }
      }
      break;

    default:
      // Other 4xx/5xx errors
      if (status >= 400) {
        const message = getErrorMessage(error);
        errorContext.showError(message, {
          autoHideDuration: 6000,
        });
      }
  }
};
